import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { TrendingUp, TrendingDown, Users, ShoppingCart, DollarSign, Package, AlertTriangle, CreditCard, Trophy, Percent, CalendarDays, HardHat, Wallet } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";

const CHART_COLORS = ["#40916c", "#e07a5f", "#f4a261", "#264653", "#e9c46a", "#8ecae6", "#ff595e", "#6a4c93"];

function KpiCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
    const tones = {
        default: "",
        good: "bg-primary/5 border-primary/30",
        warn: "bg-amber-500/5 border-amber-500/30",
        bad: "bg-destructive/5 border-destructive/30",
    };
    const iconColor = {
        default: "text-primary", good: "text-primary", warn: "text-amber-600", bad: "text-destructive",
    };
    return (
        <Card className={`p-4 ${tones[tone]}`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <div className="tiny-label truncate">{label}</div>
                    <div className="kpi-value text-2xl font-bold mt-2 font-mono truncate">{value}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-1 truncate">{sub}</div>}
                </div>
                {Icon && <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor[tone]}`} />}
            </div>
        </Card>
    );
}

export default function SalesDashboardPage() {
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [data, setData] = useState(null);

    const load = async () => {
        const [f, d] = await Promise.all([
            api.get("/farm").then(r => r.data),
            api.get("/sales-dashboard").then(r => r.data),
        ]);
        setFarm(f); setData(d);
    };
    useEffect(() => { load(); }, []);

    if (!data) return <div className="p-8 text-center text-muted-foreground">{i18n.t("batch3.9988442239f1")}</div>;

    const cur = (v) => formatCurrency(v, farm.currency);
    const k = data.kpis;
    const mom = k.mom_growth_pct;

    return (
        <div className="space-y-6" data-testid="sales-dashboard-page">
            <div>
                <div className="tiny-label">{i18n.t("batch3.32cf4b682e2b")}</div>
                <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.5651124db206")}</h1>
                <p className="text-muted-foreground mt-1">{i18n.t("batch3.129ff4d79ddb")}</p>
            </div>

            {/* KPI Grid (14 cards) */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <KpiCard label={i18n.t("batch3.24345a14377f")} value={cur(k.today_sales)} sub={`${k.today_invoices} invoices`} icon={ShoppingCart} tone="good" testid="kpi-today" />
                <KpiCard label={i18n.t("batch3.4e68dca8ab90")} value={cur(k.week_sales)} icon={TrendingUp} testid="kpi-week" />
                <KpiCard label={i18n.t("batch3.0f6cc3a89ce9")} value={cur(k.month_sales)} sub={`${k.month_invoices} invoices`} icon={CalendarDays} tone="good" testid="kpi-month" />
                <KpiCard
                    label={i18n.t("batch3.c7b6a95036d3")}
                    value={mom === null ? "—" : `${mom > 0 ? "+" : ""}${mom}%`}
                    sub={`vs. last: ${cur(k.last_month_sales)}`}
                    icon={mom !== null && mom < 0 ? TrendingDown : TrendingUp}
                    tone={mom === null ? "default" : mom < 0 ? "bad" : "good"}
                    testid="kpi-mom"
                />
                <KpiCard label={i18n.t("batch3.08ec4198dfb9")} value={cur(k.avg_order_value)} icon={DollarSign} testid="kpi-aov" />
                <KpiCard label={i18n.t("batch3.11f53e51785e")} value={cur(k.gross_profit)} sub={`${k.margin_pct}% margin`} icon={Percent} tone={k.margin_pct >= 30 ? "good" : k.margin_pct >= 15 ? "default" : "warn"} testid="kpi-profit" />
                <KpiCard label={i18n.t("batch3.8faf93a978fd")} value={cur(k.month_capex)} sub="Construction" icon={HardHat} tone={k.month_capex > 0 ? "warn" : "default"} testid="kpi-capex" />
                <KpiCard label={i18n.t("batch3.998955fd9442")} value={cur(k.net_profit)} sub="POS gross − CAPEX" icon={Wallet} tone={k.net_profit >= 0 ? "good" : "bad"} testid="kpi-net-profit" />
                <KpiCard label={i18n.t("batch3.debd497b31c2")} value={cur(k.outstanding_ar)} icon={CreditCard} tone={k.outstanding_ar > 0 ? "warn" : "default"} testid="kpi-outstanding" />
                <KpiCard label={i18n.t("batch3.18d0a9ba06b5")} value={k.transactions_today} icon={ShoppingCart} testid="kpi-transactions-today" />
                <KpiCard label={i18n.t("batch3.f81e6e6edd64")} value={k.unpaid_invoices} icon={AlertTriangle} tone={k.unpaid_invoices > 0 ? "warn" : "default"} testid="kpi-unpaid-invoices" />
                <KpiCard label={i18n.t("batch3.e300af6aa1ab")} value={k.active_customers} sub="this month" icon={Users} testid="kpi-customers" />
                <KpiCard label={i18n.t("batch3.023d1181ebc6")} value={k.low_stock_count} icon={AlertTriangle} tone={k.low_stock_count > 0 ? "bad" : "good"} testid="kpi-lowstock" />
                <KpiCard label={i18n.t("batch3.5a9469156270")} value={k.top_product} icon={Trophy} testid="kpi-topproduct" />
                <KpiCard label={i18n.t("batch3.e38c3c26615c")} value={k.today_invoices} icon={ShoppingCart} testid="kpi-invtoday" />
                <KpiCard label={i18n.t("batch3.927273a84696")} value={k.month_invoices} icon={Package} testid="kpi-invmonth" />
                <KpiCard label={i18n.t("batch3.20fe4f42d7f8")} value={cur(k.last_month_sales)} icon={CalendarDays} testid="kpi-lastmonth" />
                <KpiCard label={i18n.t("batch3.172a3d450eff")} value="—" sub="see Shifts" icon={DollarSign} testid="kpi-drawer" />
            </div>

            {/* Trend chart */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="p-4 lg:col-span-2">
                    <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />{i18n.t("batch3.3ba74080a591")}</h3>
                    <div style={{ minHeight: 260 }}>
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={data.trend_30d}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" fontSize={10} />
                                <YAxis fontSize={10} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                                <RTooltip formatter={(v, name) => name === "revenue" ? cur(v) : v} />
                                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#40916c" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4">
                    <h3 className="font-semibold mb-3">{i18n.t("batch3.e7fee63fe608")}</h3>
                    <div style={{ minHeight: 260 }}>
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={data.categories} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                                    {data.categories.map((c, i) => <Cell key={c.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Pie>
                                <RTooltip formatter={(v) => cur(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            {/* Top products + Best day */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="p-4 lg:col-span-2">
                    <h3 className="font-semibold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" />{i18n.t("batch3.f834ab20b880")}</h3>
                    <div style={{ minHeight: 240 }}>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={data.top_products} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis type="number" fontSize={10} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                                <YAxis dataKey="name" type="category" fontSize={10} width={110} />
                                <RTooltip formatter={(v) => cur(v)} />
                                <Bar dataKey="revenue" fill="#40916c" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4">
                    <h3 className="font-semibold mb-3">{i18n.t("batch3.a2e5076fdbe4")}</h3>
                    <div style={{ minHeight: 240 }}>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={data.by_dow}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="day" fontSize={11} />
                                <YAxis fontSize={10} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                                <RTooltip formatter={(v) => cur(v)} />
                                <Bar dataKey="revenue" fill="#e07a5f" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            {/* Top customers + Cashier leaderboard + Payment methods */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4">
                    <h3 className="font-semibold mb-3">{i18n.t("batch3.1eae2e68e7a6")}</h3>
                    <Table>
                        <TableHeader><TableRow><TableHead>{i18n.t("batch3.709a23220f2c")}</TableHead><TableHead className="text-right">{i18n.t("batch3.35cf82f9d2a9")}</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {data.top_customers.map((c) => (
                                <TableRow key={c.name}><TableCell className="text-xs font-semibold">{c.name}</TableCell><TableCell className="text-right font-mono">{cur(c.revenue)}</TableCell></TableRow>
                            ))}
                            {data.top_customers.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">{i18n.t("batch3.8c34947365c3")}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </Card>
                <Card className="p-4">
                    <h3 className="font-semibold mb-3">{i18n.t("batch3.c0b84858c247")}</h3>
                    <Table>
                        <TableHeader><TableRow><TableHead>{i18n.t("batch3.78c072709400")}</TableHead><TableHead className="text-right">{i18n.t("batch3.35cf82f9d2a9")}</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {data.cashiers.map((c) => (
                                <TableRow key={c.name}><TableCell className="text-xs font-semibold">{c.name}</TableCell><TableCell className="text-right font-mono">{cur(c.revenue)}</TableCell></TableRow>
                            ))}
                            {data.cashiers.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">{i18n.t("batch3.f2a3f161d5b6")}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </Card>
                <Card className="p-4">
                    <h3 className="font-semibold mb-3">{i18n.t("batch3.ad68e79c9015")}</h3>
                    <Table>
                        <TableHeader><TableRow><TableHead>{i18n.t("batch3.88306943fea7")}</TableHead><TableHead className="text-right">{i18n.t("batch3.5b0f7590d06f")}</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {data.methods.map((m) => (
                                <TableRow key={m.method}><TableCell className="text-xs font-semibold capitalize">{m.method}</TableCell><TableCell className="text-right font-mono">{cur(m.total)}</TableCell></TableRow>
                            ))}
                            {data.methods.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">{i18n.t("batch3.d48f53aaa4da")}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </Card>
            </div>
        </div>
    );
}
